import React, { useState, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';

const StepSituationPro = ({ formData, onChange }) => {
  const [localError, setLocalError] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  // État pour le secteur d'activité sélectionné
  const [selectedSector, setSelectedSector] = useState(formData.secteurActivite || '');
  const [profession, setProfession] = useState(formData.profession || '');
  // État local pour les erreurs de champs spécifiques
  const [fieldErrors, setFieldErrors] = useState({});

  // Secteurs d'activité disponibles
  const secteurs = [
    { id: "prive", label: "Secteur privé" },
    { id: "public", label: "Secteur public" },
    { id: "agricole", label: "Secteur agricole" },
    { id: "artisans", label: "Artisans - Commerçants" },
    { id: "liberales", label: "Professions libérales" },
    { id: "retraite", label: "Retraité(e) ou autres" }
  ];

  // Types de contrats selon la capture d'écran
  const contratTypes = [
    { id: "CDI", label: "CDI (Contrat à Durée Indéterminée)" },
    { id: "CDD", label: "CDD (Contrat à Durée Déterminée)" },
    { id: "Vacataire", label: "Vacataire" },
    { id: "Intérimaire", label: "Intérimaire" },
    { id: "Saisonnier", label: "Saisonnier" },
    { id: "Contrat_Pro", label: "Contrat de professionnalisation" },
    { id: "Apprentissage", label: "Contrat d'apprentissage" },
    { id: "Étudiant", label: "Étudiant" },
    { id: "Freelance", label: "Freelance/Indépendant/Auto-entrepreneur" },
    { id: "Chef_Entreprise", label: "Chef d'entreprise" },
    { id: "Libéral", label: "Profession libérale" },
    { id: "Fonctionnaire", label: "Agent public/Fonctionnaire" },
    { id: "Retraité", label: "Retraité" },
    { id: "Sans_Emploi", label: "Sans emploi" },
    { id: "Autre", label: "Autre (préciser)" }
  ];

  // Professions par secteur
  const professions = {
    prive: [
      "Cadre supérieur",
      "Ingénieur",
      "Cadre moyen",
      "Technicien",
      "Contremaître - agent de maîtrise",
      "Agent de sécurité",
      "Employé de commerce",
      "Assistante maternelle - employé de maison",
      "Employé de bureau",
      "Vendeur - caissier de magasin",
      "Ouvrier",
      "Représentant salarié",
      "Chauffeur et livreur"
    ],
    public: [
      "Cadre supérieur et professeur",
      "Cadre moyen - instituteur",
      "Infirmière et profession paramédicales",
      "Employé et agent administratif",
      "Agent de service",
      "Ouvrier d'état",
      "Agent public",
      "Aide soignant hospitalier"
    ],
    agricole: [
      "Salarié agricole",
      "Propriétaire agricole"
    ],
    artisans: [
      "Commerçant",
      "Artisan"
    ],
    liberales: [
      "Profession libérale",
      "VRP sans fixe",
      "Infirmière - Cadre moyen Secteur médical",
      "Profession libérale médicale et paramédicale"
    ],
    retraite: [
      "Étudiant",
      "Retraité secteur privé",
      "Retraité secteur public",
      "Demandeur d'emploi",
      "Invalide et pensionné",
      "Sans profession - sans emploi",
      "Divers"
    ]
  };

  // Fonction pour effacer une erreur spécifique
  const clearFieldError = (fieldName) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  // Gestionnaire de changement 
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ target: { name, value } });
    clearFieldError(name);
  };

  // Validation locale améliorée
  const validateFields = useCallback(() => {
    const errors = {};
    let isValid = true;

    // Validation du type de contrat
    if (!formData.typeContrat) {
      errors.typeContrat = 'Veuillez sélectionner un type de contrat';
      isValid = false;
    } else if (formData.typeContrat === 'Autre' && !formData.typeContratAutre) {
      errors.typeContratAutre = 'Veuillez préciser votre type de contrat';
      isValid = false;
    }
    
    // Validation du secteur d'activité
    if (!selectedSector) {
      errors.secteurActivite = 'Veuillez sélectionner un secteur d\'activité';
      isValid = false;
    }
    
    // Validation de la profession
    if (!profession) {
      errors.profession = 'Veuillez sélectionner une profession ou situation';
      isValid = false;
    }
    
    // Validation de la date d'entrée
    if (!formData.dateEntreeEntreprise) {
      errors.dateEntreeEntreprise = 'Veuillez indiquer la date de début de votre situation actuelle';
      isValid = false;
    }
    
    // Validation spécifique pour CDD
    if (formData.typeContrat === 'CDD' && !formData.dateFinContrat) {
      errors.dateFinContrat = 'Veuillez indiquer la date de fin de votre contrat';
      isValid = false;
    }
    
    // Mettre à jour les erreurs
    setFieldErrors(errors);
    setLocalError(isValid ? '' : 'Veuillez corriger les erreurs pour continuer');
    
    return isValid;
  }, [formData, selectedSector, profession]);

  // Gérer la sélection d'un secteur
  const handleSectorSelect = (sectorId) => {
    setSelectedSector(sectorId);
    onChange({ target: { name: 'secteurActivite', value: sectorId } });
    clearFieldError('secteurActivite');
    
    // Ouvrir la fenêtre modale pour choisir une profession
    setShowModal(true);
    
    // Réinitialiser la profession si on change de secteur
    if (sectorId !== selectedSector) {
      setProfession('');
      onChange({ target: { name: 'profession', value: '' } });
    }
  };
  
  // Gérer la sélection d'une profession
  const handleProfessionSelect = (prof) => {
    setProfession(prof);
    onChange({ target: { name: 'profession', value: prof } });
    clearFieldError('profession');
    setShowModal(false);
  };
  
  // Gérer le changement de type de contrat
  const handleContractTypeChange = (e) => {
    const value = e.target.value;
    handleInputChange(e);
    
    // Réinitialiser les champs spécifiques si nécessaire
    if (value !== 'CDD' && formData.dateFinContrat) {
      onChange({ target: { name: 'dateFinContrat', value: '' } });
    }
    
    if (value !== 'Autre' && formData.typeContratAutre) {
      onChange({ target: { name: 'typeContratAutre', value: '' } });
    }
  };
  
  // Ouvrir la fenêtre modale pour choisir une profession
  const openProfessionModal = () => {
    if (selectedSector) {
      setShowModal(true);
      clearFieldError('profession');
    } else {
      setFieldErrors(prev => ({
        ...prev,
        secteurActivite: 'Veuillez d\'abord sélectionner un secteur d\'activité'
      }));
    }
  };

  // Intercepter le clic sur le bouton Suivant
  useEffect(() => {
    const nextButton = document.querySelector('button.bg-orpi-primary:not(.mt-8)');
    if (nextButton) {
      const originalOnClick = nextButton.onclick;
      
      nextButton.onclick = (e) => {
        setAttemptedSubmit(true);
        const isValid = validateFields();
        
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        
        // Si valide, continuer avec le comportement normal
        if (originalOnClick) {
          return originalOnClick(e);
        }
      };
      
      // Nettoyage
      return () => {
        if (nextButton) {
          nextButton.onclick = originalOnClick;
        }
      };
    }
  }, [validateFields]);

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-orpi-secondary">
        Situation professionnelle
      </h2>

      {attemptedSubmit && localError && (
        <div className="bg-red-100 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {localError}
        </div>
      )}

      <div className="space-y-6">
        {/* Type de contrat - Toujours affiché en premier */}
        <div>
          <label className="block text-sm font-medium mb-3 text-orpi-secondary">
            Type de contrat <span className="text-red-500">*</span>
          </label>
          <select
            name="typeContrat"
            value={formData.typeContrat || ''}
            onChange={handleContractTypeChange}
            className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
              fieldErrors.typeContrat ? 'border-red-500' : ''
            }`}
            required
          >
            <option value="">Sélectionnez un type de contrat</option>
            {contratTypes.map(contrat => (
              <option key={contrat.id} value={contrat.id}>
                {contrat.label}
              </option>
            ))}
          </select>
          {fieldErrors.typeContrat && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.typeContrat}</p>
          )}
        </div>

        {/* Champ supplémentaire pour Autre type de contrat */}
        {formData.typeContrat === 'Autre' && (
          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">
              Précisez votre type de contrat <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="typeContratAutre"
              value={formData.typeContratAutre || ''}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
                fieldErrors.typeContratAutre ? 'border-red-500' : ''
              }`}
              placeholder="Précisez votre type de contrat"
              required
            />
            {fieldErrors.typeContratAutre && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.typeContratAutre}</p>
            )}
          </div>
        )}

        {/* Secteur d'activité */}
        <div>
          <label className="block text-sm font-medium mb-3 text-orpi-secondary">
            Secteur d'activité <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            {secteurs.map((secteur) => (
              <button
                key={secteur.id}
                type="button"
                className={`p-3 border rounded-lg hover:border-orpi-primary ${
                  selectedSector === secteur.id ? 'border-orpi-primary bg-orpi-light' : ''
                } ${fieldErrors.secteurActivite ? 'border-red-300' : ''}`}
                onClick={() => handleSectorSelect(secteur.id)}
              >
                {secteur.label}
              </button>
            ))}
          </div>
          {fieldErrors.secteurActivite && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.secteurActivite}</p>
          )}
        </div>

        {/* Profession ou situation */}
        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Profession ou situation <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={openProfessionModal}
            className={`w-full p-3 flex justify-between items-center border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
              fieldErrors.profession ? 'border-red-500' : ''
            }`}
          >
            <span className={profession ? 'text-black' : 'text-gray-400'}>
              {profession || "Choisir une profession ou une situation"}
            </span>
            <span>›</span>
          </button>
          {fieldErrors.profession && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.profession}</p>
          )}
        </div>

        {/* Date de fin de contrat pour CDD */}
        {formData.typeContrat === 'CDD' && (
          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">
              Date de fin de contrat <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="dateFinContrat"
              value={formData.dateFinContrat || ''}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
                fieldErrors.dateFinContrat ? 'border-red-500' : ''
              }`}
              min={new Date().toISOString().split('T')[0]}
              required
            />
            {fieldErrors.dateFinContrat && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.dateFinContrat}</p>
            )}
          </div>
        )}

        {/* Date d'entrée */}
        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Chez l'employeur actuel ou dans la même situation depuis <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="dateEntreeEntreprise"
            value={formData.dateEntreeEntreprise || ''}
            onChange={handleInputChange}
            className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
              fieldErrors.dateEntreeEntreprise ? 'border-red-500' : ''
            }`}
            required
            max={new Date().toISOString().split('T')[0]}
          />
          {fieldErrors.dateEntreeEntreprise && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.dateEntreeEntreprise}</p>
          )}
        </div>
        
        <p className="text-sm text-gray-500 mt-4">
          <span className="text-red-500">*</span> Champs obligatoires
        </p>
      </div>

      {/* Fenêtre modale pour choisir une profession */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Choisissez votre profession ou votre situation</h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-2">
              {selectedSector && professions[selectedSector] && professions[selectedSector].map((prof, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-full text-left p-3 border rounded-lg hover:bg-orpi-light hover:border-orpi-primary"
                  onClick={() => handleProfessionSelect(prof)}
                >
                  {prof}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepSituationPro;